import React from 'react'
import {
  Box,
  VStack,
  HStack,
  Icon,
  Heading,
  useColorModeValue,
} from '@chakra-ui/react'
import { InfoIcon } from '@chakra-ui/icons'
import 'katex/dist/katex.min.css'
import { InlineMath, BlockMath } from 'react-katex'

interface ProblemDescriptionProps {
  content: string
}

export default function ProblemDescription({ content }: ProblemDescriptionProps) {
  const textColor = useColorModeValue('gray.800', 'gray.200')

  // Function to parse content and render LaTeX
  const renderContent = (text: string) => {
    if (!text) return null

    // Split content by LaTeX delimiters - handle $$ first, then $
    const parts = text.split(/(\$\$.*?\$\$)/g)
    
    return parts.map((part, index) => {
      if (part.startsWith('$$') && part.endsWith('$$')) {
        // Block math (display mode) - centered on its own line
        const math = part.slice(2, -2)
        return (
          <Box key={index} my={4} display="flex" justifyContent="center">
            <BlockMath math={math} />
          </Box>
        )
      } else {
        // Regular text - further split by inline math
        const subParts = part.split(/(\$[^$\n]*?\$)/g)
        return subParts.map((subPart, subIndex) => {
          if (subPart.startsWith('$') && subPart.endsWith('$')) {
            // Inline math - flows with text
            const math = subPart.slice(1, -1)
            return (
              <InlineMath key={`${index}-${subIndex}`} math={math} />
            )
          } else {
            // Regular text - use span for inline flow
            return (
              <Box
                key={`${index}-${subIndex}`}
                as="span"
                color={textColor}
                dangerouslySetInnerHTML={{ __html: formatText(subPart) }}
                sx={{
                  'h1, h2, h3, h4, h5, h6': {
                    display: 'block',
                    fontWeight: 'bold',
                    mt: 4,
                    mb: 2,
                    color: textColor,
                  },
                  'h1': { fontSize: 'xl' },
                  'h2': { fontSize: 'lg' },
                  'h3': { fontSize: 'md' },
                  'p': {
                    display: 'block',
                    mb: 3,
                    lineHeight: 1.6,
                    color: textColor,
                  },
                  'ul, ol': {
                    display: 'block',
                    mb: 3,
                    pl: 6,
                  },
                  'li': {
                    display: 'list-item',
                    mb: 1,
                    color: textColor,
                  },
                  'code': {
                    bg: 'gray.100',
                    px: 1,
                    py: 0.5,
                    borderRadius: 'sm',
                    fontSize: 'sm',
                    fontFamily: 'mono',
                    color: 'gray.800',
                  },
                  'pre': {
                    display: 'block',
                    bg: 'gray.100',
                    p: 3,
                    borderRadius: 'md',
                    overflowX: 'auto',
                    mb: 3,
                    fontSize: 'sm',
                    fontFamily: 'mono',
                    color: 'gray.800',
                  },
                  'blockquote': {
                    display: 'block',
                    borderLeft: '4px solid',
                    borderColor: 'gray.300',
                    pl: 4,
                    py: 2,
                    mb: 3,
                    bg: 'gray.50',
                  },
                  'table': {
                    display: 'block',
                    borderCollapse: 'collapse',
                    width: '100%',
                    mb: 3,
                  },
                  'th, td': {
                    border: '1px solid',
                    borderColor: 'gray.300',
                    px: 2,
                    py: 1,
                    textAlign: 'left',
                    color: textColor,
                  },
                  'th': {
                    bg: 'gray.100',
                    fontWeight: 'bold',
                  },
                }}
              />
            )
          }
        })
      }
    })
  }

  // Function to format regular text (markdown-like formatting)
  const formatText = (text: string): string => {
    if (!text) return ''

    return text
      // Headers
      .replace(/^### (.*$)/gim, '<h3>$1</h3>')
      .replace(/^## (.*$)/gim, '<h2>$1</h2>')
      .replace(/^# (.*$)/gim, '<h1>$1</h1>')
      
      // Tables - handle markdown table format
      .replace(/(\|.*\|[\r\n]+\|[\s\-:|]+\|[\r\n]+(\|.*\|[\r\n]+)*)/g, (match) => {
        const lines = match.trim().split('\n').filter(line => line.trim())
        if (lines.length < 2) return match // Need at least header and separator
        
        const headerLine = lines[0] || ''
        const separatorLine = lines[1] || ''
        const dataLines = lines.slice(2)
        
        if (!headerLine) return match
        
        // Parse header
        const headers = headerLine.split('|').filter(cell => cell.trim()).map(cell => cell.trim())
        
        // Parse data rows
        const rows = dataLines.map(line => 
          line.split('|').filter(cell => cell.trim()).map(cell => cell.trim())
        )
        
        // Build HTML table
        let tableHtml = '<table><thead><tr>'
        headers.forEach(header => {
          tableHtml += `<th>${header}</th>`
        })
        tableHtml += '</tr></thead><tbody>'
        
        rows.forEach(row => {
          tableHtml += '<tr>'
          row.forEach(cell => {
            tableHtml += `<td>${cell}</td>`
          })
          tableHtml += '</tr>'
        })
        
        tableHtml += '</tbody></table>'
        return tableHtml
      })
      
      // Bold and italic
      .replace(/\*\*(.*?)\*\*/g, '<strong>$1</strong>')
      .replace(/\*(.*?)\*/g, '<em>$1</em>')
      
      // Code blocks
      .replace(/```([\s\S]*?)```/g, '<pre><code>$1</code></pre>')
      .replace(/`([^`]+)`/g, '<code>$1</code>')
      
      // Links
      .replace(/\[([^\]]+)\]\(([^)]+)\)/g, '<a href="$2" target="_blank" rel="noopener noreferrer" style="color: blue; text-decoration: underline;">$1</a>')
      
      // Line breaks
      .replace(/\n/g, '<br>')
  }

  return (
    <VStack align="stretch" spacing={4} h="100%">
      <HStack flexShrink={0}>
        <Icon as={InfoIcon} color="blue.400" />
        <Heading size="md" color={textColor}>Problem Description</Heading>
      </HStack>
      <Box
        flex={1}
        overflowY="auto"
        overflowX="hidden"
        pr={2}
        minH={0}
      >
        {renderContent(content)}
      </Box>
    </VStack>
  )
} 